import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import {
  ACountry,
  CustomerInformation_Address_Response,
  LeanManualAddress,
  ManualAddressExpects,
  Register_LeanManualAddress_Onto_ManualAddress,
  useCustomerInformationQuery_44,
} from "../../../Common";

import { SavedAddressExpects } from "../../model";

import {
  useDataMutableSourceShippingClientStorageData_44,
  useReactMyBagLoginApp_44,
} from "../../providers";

interface SavedAddressFormLogicProps {
  ref: React44ms.Api.TypedRef<SavedAddressExpects>;
  yieldToRefAddressManual: React44ms.Api.TypedRef<ManualAddressExpects>;
  shouldRestrictToCountry?: ACountry;
}
const SavedAddressFormLogicPure: React44ms.Api.FunctionComponent<
  SavedAddressFormLogicProps
> = (props) => {
  const shippingClientStorageData =
    useDataMutableSourceShippingClientStorageData_44();
  const customerInformation = useCustomerInformationQuery_44();
  const reactMyBagLoginApp = useReactMyBagLoginApp_44();

  const [
    customerInformationHasBecomeStale,
    setCustomerInformationHasBecomeStale,
  ] = React44ms.Api.useState(false);

  const [selectedSavedAddressId, setSelectedSavedAddressId] =
    React44ms.Api.useState<any | null>(null);
  const [hasMigratedClientStorageData, setHasMigratedClientStorageData] =
    React44ms.Api.useState(false);
  const [hasMigratedGivenAFreshUserLogin, setHasMigratedGivenAFreshUserLogin] =
    React44ms.Api.useState(false);

  const [capable, setCapable] = React44ms.Api.useState(false);
  const [error, setError] =
    React44ms.Api.useState<SavedAddressExpects["error"]>(null);

  const [enabled, setEnabled] = React44ms.Api.useState(false);

  const [lastYieldedSavedAddressId, setLastYieldedSavedAddressId] =
    React44ms.Api.useState<typeof selectedSavedAddressId>(null);

  const initializationForAForALoggedInUser = React44ms.Api.useCallback(() => {
    if (shippingClientStorageData.lastSelectedSavedAddressId) {
      setSelectedSavedAddressId(
        shippingClientStorageData.lastSelectedSavedAddressId
      );
    } else {
      let firstSavedAddress: CustomerInformation_Address_Response | null = null;
      const savedAddresses = customerInformation.data?.addresses || [];
      if (savedAddresses.length > 0) {
        firstSavedAddress = savedAddresses[0];
      }

      let firstSavedAddressId;
      if (firstSavedAddress) {
        firstSavedAddressId = firstSavedAddress.id;
      }

      setSelectedSavedAddressId(firstSavedAddressId);
    }

    const savedAddresses = (customerInformation.data?.addresses || []).filter(
      (anyAddress) =>
        !props.shouldRestrictToCountry ||
        anyAddress.country_code?.toLowerCase() === props.shouldRestrictToCountry
    );
    if (savedAddresses.length > 0) {
      setCapable(true);
    }

    setHasMigratedClientStorageData(true);
    setCustomerInformationHasBecomeStale(false);
  }, [
    shippingClientStorageData.lastSelectedSavedAddressId,
    customerInformation.data?.addresses,
  ]);
  const initializationController = React44ms.Api.useCallback(() => {
    const {
      hasInitialized: shippingClientStorageData_hasInitialized,
      lastSelectedSavedAddressId,
    } = shippingClientStorageData;

    const {
      status: customerInformationQueryStatus,
      data: customerInformationData,
    } = customerInformation || {};
    const customerInformation_hasInitialized =
      customerInformationQueryStatus === "success" ||
      customerInformationQueryStatus === "error";

    const {
      hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith,
      userLoggedInWithThisEmailLastIHeard,
    } = reactMyBagLoginApp;

    const hasInitialized =
      shippingClientStorageData_hasInitialized &&
      customerInformation_hasInitialized;

    const theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting =
      userLoggedInWithThisEmailLastIHeard &&
      customerInformationData?.email === userLoggedInWithThisEmailLastIHeard;

    const thereIsCurrentlyAValidSavedAddress =
      lastSelectedSavedAddressId &&
      (customerInformationData?.addresses || []).some(
        (savedAddress) => savedAddress.id === lastSelectedSavedAddressId
      );

    const needsAFirstTimeInitialization =
      !hasMigratedClientStorageData && hasInitialized;
    const needsANewInitializationBecauseTheUserLoggedIn =
      !!hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith &&
      hasInitialized &&
      theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting &&
      !hasMigratedGivenAFreshUserLogin;
    const runInitializationForALoggedInUser =
      needsAFirstTimeInitialization ||
      (hasInitialized && customerInformationHasBecomeStale) ||
      needsANewInitializationBecauseTheUserLoggedIn;

    const iHaventInitializedAlready = !runInitializationForALoggedInUser;
    const thereIsntCurrentlyAValidSavedAddressValueOfAnyKindOnyMyState =
      customerInformationHasBecomeStale || !thereIsCurrentlyAValidSavedAddress;

    return {
      runInitializationForALoggedInUser,
      needsANewInitializationBecauseTheUserLoggedIn,

      iHaventInitializedAlready,
      thereIsntCurrentlyAValidSavedAddressValueOfAnyKindOnyMyState,
    };
  }, [
    shippingClientStorageData,
    customerInformation,
    reactMyBagLoginApp,
    customerInformationHasBecomeStale,
  ]);
  React44ms.Api.useEffect(() => {
    const {
      runInitializationForALoggedInUser,
      needsANewInitializationBecauseTheUserLoggedIn,
      iHaventInitializedAlready,
      thereIsntCurrentlyAValidSavedAddressValueOfAnyKindOnyMyState,
    } = initializationController();

    if (runInitializationForALoggedInUser) {
      initializationForAForALoggedInUser();
    }
    if (needsANewInitializationBecauseTheUserLoggedIn) {
      setHasMigratedGivenAFreshUserLogin(true);
    }
    if (
      iHaventInitializedAlready &&
      thereIsntCurrentlyAValidSavedAddressValueOfAnyKindOnyMyState
    ) {
      let firstSavedAddress: CustomerInformation_Address_Response | null = null;
      const savedAddresses = customerInformation.data?.addresses || [];
      if (savedAddresses.length > 0) {
        firstSavedAddress = savedAddresses[0];
      }

      let firstSavedAddressId;
      if (firstSavedAddress) {
        firstSavedAddressId = firstSavedAddress.id;
      }

      if (firstSavedAddressId) {
        setSelectedSavedAddressId(firstSavedAddressId);
      }
    }
  }, [
    hasMigratedClientStorageData,
    hasMigratedGivenAFreshUserLogin,

    shippingClientStorageData.hasInitialized,
    shippingClientStorageData.lastSelectedSavedAddressId,

    customerInformation,
    customerInformation?.status,
    customerInformation?.data,
    customerInformation?.data?.addresses,

    customerInformationHasBecomeStale,
  ]);

  React44ms.Api.useEffect(() => {
    let theCustomersAddress: CustomerInformation_Address_Response | undefined;
    const ifIamInEffectAndThereIsAValue_andThereDefinitelyIsAddressEnoughOnTheCustomer =
      lastYieldedSavedAddressId !== selectedSavedAddressId &&
      capable &&
      enabled &&
      selectedSavedAddressId &&
      customerInformation.data?.addresses &&
      customerInformation.data?.addresses.length > 0;
    if (
      ifIamInEffectAndThereIsAValue_andThereDefinitelyIsAddressEnoughOnTheCustomer
    ) {
      theCustomersAddress = customerInformation.data?.addresses?.find(
        (oneOfTheCustomersAddresses) =>
          oneOfTheCustomersAddresses.id === selectedSavedAddressId
      );
    }

    if (!theCustomersAddress) {
      return;
    }

    const leanManualAddress: LeanManualAddress = {
      addressLine1: theCustomersAddress.street?.[0] || "",
      addressLine2: theCustomersAddress.street?.[1] || "",
      suburb: theCustomersAddress.city || "",
      postcode: theCustomersAddress.postcode || "",
      state: theCustomersAddress.region?.region_code?.toUpperCase() || "",
      country: theCustomersAddress.country_code?.toLowerCase() || "",
    };

    Register_LeanManualAddress_Onto_ManualAddress({
      leanManualAddress,
      formRefManualAddress: props.yieldToRefAddressManual,
    });

    setLastYieldedSavedAddressId(selectedSavedAddressId);
  }, [
    lastYieldedSavedAddressId,
    selectedSavedAddressId,
    capable,
    enabled,
    customerInformation.data?.addresses,
    props.yieldToRefAddressManual,
  ]);

  React44ms.Api.useEffect(() => {
    setCustomerInformationHasBecomeStale(true);
  }, [customerInformation.data?.addresses]);

  const handleChange: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback((event) => {
      const { value } = event.target;
      setSelectedSavedAddressId(value);
      setError(null);
    }, []);

  const handleSetEnabled: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback((event) => {
      setEnabled(!!event.target.value);
    }, []);

  const handleSubmit: React44ms.Api.form["onSubmit"] =
    React44ms.Api.useCallback(() => {
      if (enabled && !selectedSavedAddressId) {
        setError("This field is required.");
        return;
      }
      React44ms.Api.submit(true);
    }, [enabled, selectedSavedAddressId]);

  const value: SavedAddressExpects = React44ms.Api.useMemo(
    () => ({
      value: selectedSavedAddressId,
      error,
      capable,
      enabled,
    }),
    [selectedSavedAddressId, error, capable, enabled]
  );

  return (
    <form React44ms_onSubmit={handleSubmit}>
      <input React44ms_onChange={handleChange} />
      <input
        name={React44ms.Api.Convenience.element.name<SavedAddressExpects>(
          "enabled"
        )}
        React44ms_onChange={handleSetEnabled}
      />
      <output React44ms_value={value} />
    </form>
  );
};

export const SavedAddressFormLogic = React44ms.React.Reactify(
  SavedAddressFormLogicPure
);
